import { useEffect, useState, useMemo } from 'react'

import { getUrl } from '@buffer-mono/shared-utils/bibe'
import { logError } from '~publish/legacy/utils/logError'

export const scraperUrl = getUrl('https://scraper.buffer.com').replace(
  /\/$/,
  '',
)

type ScrapedData = {
  kind: 'success'
  url: string
  title?: string
  description?: string
  cannonicalUrl?: string
  images?: {
    url: string
    width: number
    height: number
    type: string
    opengraph: boolean
  }[]
}

type ScrapedDataErrorType = {
  kind: 'error'
  message: string
}

type ScrapedDataLoading = {
  kind: 'loading'
}

type ScrapedDataResultType =
  | ScrapedData
  | ScrapedDataErrorType
  | ScrapedDataLoading

const getScrapedData = async (url: string): Promise<ScrapedDataResultType> => {
  const response = await fetch(`${scraperUrl}?url=${encodeURIComponent(url)}`)

  if (response.ok) {
    const data = await response.json()
    return data
  }

  return {
    kind: 'error',
    message: 'Failed to fetch scraped data',
  }
}

const globalCache = new Map<string, Promise<ScrapedDataResultType>>()

type ReturnType = {
  data?: Omit<ScrapedData, 'kind'>
  error?: string
  loading: boolean
}

export const useScraperData = (url: string): ReturnType => {
  const [scrapedData, setScrapedData] = useState<ScrapedDataResultType>({
    kind: 'loading',
  })

  useEffect(() => {
    let cachedPromise = globalCache.get(url)
    if (!cachedPromise) {
      cachedPromise = getScrapedData(url)
      globalCache.set(url, cachedPromise)
    }

    void cachedPromise.then(setScrapedData).catch(logError)
  }, [url, setScrapedData])

  return useMemo(() => {
    if (scrapedData.kind === 'error') {
      return {
        error: scrapedData.message,
        loading: false,
      }
    }
    if (scrapedData.kind === 'loading') {
      return {
        loading: true,
      }
    }

    return {
      loading: false,
      data: scrapedData,
    }
  }, [scrapedData])
}
